import type { ArmDetection, HandDetection } from "../api/types";

/**
 * Overlay drawing, in source-pixel coordinates.
 *
 * The canvas is sized to the original recording's resolution, so these
 * functions receive landmark coordinates exactly as the analysis produced them
 * — no scaling, and nothing to get wrong when the panel is resized or zoomed.
 */

const HAND_COLOURS: Record<string, string> = {
  left: "#4cc4ff",
  right: "#ffb35c",
};

export function drawHands(
  ctx: CanvasRenderingContext2D,
  hands: HandDetection[],
  connections: [number, number][],
  options: { showIndices?: boolean; scale?: number } = {},
) {
  const s = options.scale ?? Math.max(1, ctx.canvas.width / 640);
  for (const hand of hands) {
    const colour = HAND_COLOURS[hand.handedness] ?? "#4cc4ff";
    ctx.strokeStyle = colour;
    ctx.lineWidth = 2 * s;
    ctx.lineCap = "round";
    // Landmarks arrive as a flat [x0, y0, x1, y1, …] array; with every frame
    // analysed this is tens of thousands of points and the nested form tripled
    // the payload.
    const count = hand.xy.length / 2;
    ctx.beginPath();
    for (const [a, b] of connections) {
      if (a >= count || b >= count) continue;
      ctx.moveTo(hand.xy[a * 2], hand.xy[a * 2 + 1]);
      ctx.lineTo(hand.xy[b * 2], hand.xy[b * 2 + 1]);
    }
    ctx.stroke();

    for (let i = 0; i < count; i++) {
      const x = hand.xy[i * 2];
      const y = hand.xy[i * 2 + 1];
      ctx.fillStyle = i === 0 ? "#ffffff" : colour;
      ctx.beginPath();
      ctx.arc(x, y, (i === 0 ? 4 : 2.6) * s, 0, Math.PI * 2);
      ctx.fill();
      if (options.showIndices) {
        ctx.fillStyle = "#ffffffcc";
        ctx.font = `${9 * s}px ui-monospace, monospace`;
        ctx.fillText(String(i), x + 4 * s, y - 4 * s);
      }
    }

    const wrist: [number, number] | null = count > 0 ? [hand.xy[0], hand.xy[1]] : null;
    if (wrist) {
      const label = `${hand.handedness} ${(hand.score * 100).toFixed(0)}%`;
      ctx.font = `${11 * s}px ui-monospace, monospace`;
      const width = ctx.measureText(label).width + 8 * s;
      ctx.fillStyle = "#0c0e12cc";
      ctx.fillRect(wrist[0] - width / 2, wrist[1] + 8 * s, width, 15 * s);
      ctx.fillStyle = colour;
      ctx.textAlign = "center";
      ctx.fillText(label, wrist[0], wrist[1] + 19 * s);
      ctx.textAlign = "start";
    }
  }
}

/**
 * Arms are drawn only where the two eyes agreed about the joint.
 *
 * The pose model happily returns a shoulder in the middle of an egocentric
 * frame with 0.99 visibility. Drawing that would be inventing a limb, so a
 * joint the stereo check rejected is shown — if at all — as a hollow marker
 * with its reason, never as part of a skeleton.
 */
export function drawArms(
  ctx: CanvasRenderingContext2D,
  arms: ArmDetection[],
  accepted: Set<string>,
  connections: [string, string][],
  options: { showRejected?: boolean; scale?: number } = {},
) {
  const s = options.scale ?? Math.max(1, ctx.canvas.width / 640);
  const byName = new Map(arms.map((a) => [a.name, a]));

  ctx.strokeStyle = "#9be08a";
  ctx.lineWidth = 3 * s;
  ctx.beginPath();
  for (const [a, b] of connections) {
    if (!accepted.has(a) || !accepted.has(b)) continue;
    const p = byName.get(a);
    const q = byName.get(b);
    if (!p || !q) continue;
    ctx.moveTo(p.xy[0], p.xy[1]);
    ctx.lineTo(q.xy[0], q.xy[1]);
  }
  ctx.stroke();

  for (const joint of arms) {
    const ok = accepted.has(joint.name);
    if (!ok && !options.showRejected) continue;
    ctx.beginPath();
    ctx.arc(joint.xy[0], joint.xy[1], 5 * s, 0, Math.PI * 2);
    if (ok) {
      ctx.fillStyle = "#9be08a";
      ctx.fill();
    } else {
      ctx.strokeStyle = "#6f7889";
      ctx.setLineDash([3 * s, 3 * s]);
      ctx.lineWidth = 1.5 * s;
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.fillStyle = ok ? "#9be08a" : "#6f7889";
    ctx.font = `${10 * s}px ui-monospace, monospace`;
    ctx.fillText(joint.name.replace("_", " "), joint.xy[0] + 8 * s, joint.xy[1] + 3 * s);
  }
}
